import { useSyncExternalStore } from "react";

/**
 * Home-screen install state. Chrome/Edge fire `beforeinstallprompt` once,
 * early in page load — often before React has mounted anything — so this
 * module is imported from main.tsx to start listening straight away and
 * keeps the event until a component asks to show the browser's install
 * dialog. Safari never fires it; iPhone users get written steps instead.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface InstallState {
  /** Running as the home-screen app (or an installed desktop PWA). */
  installed: boolean;
  /** The browser offered a one-tap install (Chrome/Edge on Android or desktop). */
  canPrompt: boolean;
}

function readStandalone(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

let deferred: BeforeInstallPromptEvent | null = null;
let state: InstallState = { installed: readStandalone(), canPrompt: false };
const listeners = new Set<() => void>();

function set(next: Partial<InstallState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

window.addEventListener("beforeinstallprompt", (e) => {
  // Stop Chrome's own mini-infobar; we show our card instead.
  e.preventDefault();
  deferred = e as BeforeInstallPromptEvent;
  set({ canPrompt: true });
});

window.addEventListener("appinstalled", () => {
  deferred = null;
  set({ installed: true, canPrompt: false });
});

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useInstallState(): InstallState {
  return useSyncExternalStore(subscribe, () => state);
}

/** Opens the browser's install dialog. Resolves true if the user accepted. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const e = deferred;
  // The event can only be used once, accepted or not.
  deferred = null;
  set({ canPrompt: false });
  await e.prompt();
  const { outcome } = await e.userChoice;
  return outcome === "accepted";
}

/** iPhone or iPad (iPadOS reports itself as a Mac, so check for touch too). */
export function isIOS(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  );
}
